/**
 * AdvancedMapEditor - Map editing component with path drawing
 *
 * Simplified workflow:
 * 1. Click draw button to start drawing
 * 2. Click to add points
 * 3. Click first point to close (polygon) OR Enter/✓ to finish (polyline)
 * 4. After drawing, show style panel to customize the path
 * 5. Paths panel on right side for managing all paths
 */
import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react'
import L from 'leaflet'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import {
  HiOutlineX,
  HiOutlineTrash,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineColorSwatch,
  HiOutlinePencil,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
} from 'react-icons/hi'
import { TbPolygon, TbLine } from 'react-icons/tb'

import LeafletMarketMapEditor from './LeafletMarketMapEditor'
import { DrawingToolbar, useLeafletDraw } from './drawing'
import marketMapService from '../../services/marketMapService'
import mapService from '../../services/mapService'

// Preset colors for quick selection
const PRESET_COLORS = [
  { name: 'Đỏ', stroke: '#EF4444', fill: '#FEE2E2' },
  { name: 'Cam', stroke: '#F97316', fill: '#FFEDD5' },
  { name: 'Vàng', stroke: '#EAB308', fill: '#FEF9C3' },
  { name: 'Xanh lá', stroke: '#22C55E', fill: '#DCFCE7' },
  { name: 'Xanh dương', stroke: '#3B82F6', fill: '#DBEAFE' },
  { name: 'Tím', stroke: '#8B5CF6', fill: '#EDE9FE' },
  { name: 'Hồng', stroke: '#EC4899', fill: '#FCE7F3' },
  { name: 'Xám', stroke: '#6B7280', fill: '#F3F4F6' },
]

// Stroke styles
const STROKE_STYLES = [
  { value: 'solid', label: 'Liền', icon: '━━━' },
  { value: 'dashed', label: 'Nét đứt', icon: '╌╌╌' },
  { value: 'dotted', label: 'Chấm', icon: '···' },
]

// Animation options
const ANIMATION_OPTIONS = [
  { value: 'none', label: 'Không' },
  { value: 'pulse', label: 'Nhấp nháy' },
  { value: 'march', label: 'Chạy viền' },
  { value: 'glow', label: 'Phát sáng' },
]

const AdvancedMapEditor = forwardRef(function AdvancedMapEditor(
  {
    mapId,
    markers = [],
    utilityMarkers = [],
    center,
    zoom,
    mapStyle,
    showCenterMarker = true,
    onCenterChange,
    onZoomChange,
    onMarkerClick,
    onUtilityMarkerClick,
    onTempMarkerConfirm,
    onMoveEnd, // Called when map stops moving
    addingMarker = false,
    // Use 'market' for admin market maps or 'property' for user property maps
    mapType = 'market',
    // Distance line for distance calculator
    distanceLine = null,
  },
  ref
) {
  // Select API service based on map type
  const apiService = mapType === 'property' ? mapService : marketMapService
  const leafletMapRef = useRef(null)
  const mapInstanceRef = useRef(null)

  // State
  const [paths, setPaths] = useState([]) // All drawn paths
  const [selectedPath, setSelectedPath] = useState(null)
  const [showStylePanel, setShowStylePanel] = useState(false)
  const [showPathsPanel, setShowPathsPanel] = useState(false) // Panel quản lý paths - ẩn mặc định
  const [newlyCreatedPath, setNewlyCreatedPath] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [editingPathName, setEditingPathName] = useState(null)
  const [mapReady, setMapReady] = useState(false) // Track map ready state

  // Initialize Leaflet Draw hook - pass map state so it re-initializes when map becomes ready
  const {
    isDrawing,
    pointCount,
    canSnapClose,
    startDrawing,
    finishDrawing,
    cancelDrawing,
    addShape,
    removeShape,
    updateShapeStyle,
    clearAllShapes,
    setShapeVisibility,
    fitToShape,
  } = useLeafletDraw({
    map: mapReady ? mapInstanceRef.current : null,
    onShapeCreated: handleShapeCreated,
    onShapeSelected: handleShapeSelected,
    defaultStyle: {
      strokeColor: '#3B82F6',
      fillColor: '#DBEAFE',
      strokeWidth: 3,
      fillOpacity: 0.3,
    },
  })

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    ...leafletMapRef.current,
    getPaths: () => paths,
    clearAllPaths: () => {
      clearAllShapes()
      setPaths([])
    },
  }))

  // Get map instance when ready
  useEffect(() => {
    const checkMap = setInterval(() => {
      if (leafletMapRef.current) {
        const map = leafletMapRef.current.getMap()
        if (map) {
          mapInstanceRef.current = map
          setMapReady(true)
          clearInterval(checkMap)
        }
      }
    }, 100)

    return () => clearInterval(checkMap)
  }, [])

  // Load existing paths when mapId changes AND map is ready
  useEffect(() => {
    if (mapId && mapReady) {
      // Small delay to ensure drawnItems is initialized
      const timer = setTimeout(() => {
        loadPaths()
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [mapId, mapReady])

  // Load paths from API
  const loadPaths = async () => {
    if (!mapId) return

    setIsLoading(true)
    try {
      const response = await apiService.getLayers(mapId)
      if (response.status === 'success') {
        const loadedLayers = response.data || []
        const allPaths = []

        loadedLayers.forEach(layer => {
          layer.shapes?.forEach(shape => {
            // Get style from shape or use defaults
            const shapeStyle = shape.style || {}
            const strokeColor = shapeStyle.stroke_color || shape.stroke_color || '#3B82F6'
            const fillColor = shapeStyle.fill_color || shape.fill_color || '#DBEAFE'
            const strokeWidth = shapeStyle.stroke_width || shape.stroke_width || 3
            const fillOpacity = shapeStyle.fill_opacity || shape.fill_opacity || 0.3
            const strokeStyle = shapeStyle.stroke_style || shape.stroke_style || 'solid'
            const animation = shapeStyle.animation || shape.animation || 'none'
            // show_label: default true if not specified
            const showLabel = shape.show_label !== false

            const pathData = {
              id: shape.id,
              layerId: layer.id,
              name: shape.name || `${shape.shape_type === 'polygon' ? 'Vùng' : 'Đường'} ${shape.id}`,
              shapeType: shape.shape_type,
              geojson: shape.geojson,
              visible: shape.is_visible !== false,
              style: {
                strokeColor,
                fillColor,
                strokeWidth,
                fillOpacity,
                strokeStyle,
                animation,
                showLabel,
              },
            }
            allPaths.push(pathData)

            // Add to map
            addShape({
              id: shape.id,
              shapeType: shape.shape_type,
              geojson: shape.geojson,
              style: {
                stroke_color: strokeColor,
                fill_color: fillColor,
                stroke_width: strokeWidth,
                fill_opacity: fillOpacity,
                stroke_style: strokeStyle,
              },
            })
          })
        })

        setPaths(allPaths)
      }
    } catch (error) {
      console.error('Failed to load paths:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Start drawing a new path
  const handleStartDrawing = useCallback(async () => {
    // Ensure we have a layer
    if (mapId) {
      try {
        const response = await apiService.getLayers(mapId)
        if (response.status === 'success' && response.data.length === 0) {
          await apiService.createLayer(mapId, {
            name: 'Paths',
            layer_type: 'custom',
            default_stroke_color: '#3B82F6',
            default_fill_color: '#DBEAFE',
          })
        }
      } catch (error) {
        console.error('Failed to ensure layer:', error)
      }
    }

    startDrawing('polyline', {
      strokeColor: '#3B82F6',
      fillColor: '#DBEAFE',
    })
  }, [mapId, startDrawing])

  // Handle shape created
  async function handleShapeCreated(data) {
    const isClosed = data.shapeType === 'polygon' || data.isClosed

    const newPath = {
      id: `temp_${Date.now()}`,
      name: isClosed ? 'Vùng mới' : 'Đường mới',
      shapeType: isClosed ? 'polygon' : 'polyline',
      geojson: data.geoJSON,
      visible: true,
      style: {
        strokeColor: data.style.color || '#3B82F6',
        fillColor: data.style.fillColor || '#DBEAFE',
        strokeWidth: data.style.weight || 3,
        fillOpacity: data.style.fillOpacity || 0.3,
        strokeStyle: 'solid',
        animation: 'none',
      },
      layer: data.layer,
    }

    setNewlyCreatedPath(newPath)
    setSelectedPath(newPath)
    setShowStylePanel(true)
  }

  // Handle shape selected
  function handleShapeSelected(shapeData) {
    if (shapeData) {
      const path = paths.find(p => p.id === shapeData.id)
      if (path) {
        setSelectedPath(path)
        setShowStylePanel(true)
      }
    }
  }

  // Save path to backend
  const savePath = async (pathData) => {
    if (!mapId) {
      toast.error('Vui lòng lưu bản đồ trước')
      return null
    }

    try {
      const layersResponse = await apiService.getLayers(mapId)
      let layerId = layersResponse.data?.[0]?.id

      if (!layerId) {
        const newLayer = await apiService.createLayer(mapId, {
          name: 'Paths',
          layer_type: 'custom',
        })
        layerId = newLayer.data.id
      }

      const response = await apiService.createShape(mapId, {
        layer_id: layerId,
        name: pathData.name,
        shape_type: pathData.shapeType,
        geojson: pathData.geojson,
        stroke_color: pathData.style.strokeColor,
        fill_color: pathData.style.fillColor,
        stroke_width: pathData.style.strokeWidth,
        fill_opacity: pathData.style.fillOpacity,
        stroke_style: pathData.style.strokeStyle || 'solid',
        animation: pathData.style.animation || 'none',
        show_label: pathData.style.showLabel !== false, // Default true
      })

      if (response.status === 'success') {
        return response.data
      }
    } catch (error) {
      console.error('Failed to save path:', error)
      toast.error('Không thể lưu đường vẽ')
    }
    return null
  }

  // Confirm and save newly created path
  const handleConfirmNewPath = async () => {
    if (!newlyCreatedPath) return

    const saved = await savePath(newlyCreatedPath)
    if (saved) {
      const updatedPath = { ...newlyCreatedPath, id: saved.id }
      setPaths(prev => [...prev, updatedPath])
      toast.success('Đã lưu thành công!')
    }

    setNewlyCreatedPath(null)
    setShowStylePanel(false)
    setSelectedPath(null)
  }

  // Cancel newly created path
  const handleCancelNewPath = () => {
    if (newlyCreatedPath?.layer) {
      removeShape(newlyCreatedPath.id)
    }
    setNewlyCreatedPath(null)
    setShowStylePanel(false)
    setSelectedPath(null)
  }

  // Update path style
  const handleUpdateStyle = async (styleUpdate) => {
    if (!selectedPath) return

    const updatedStyle = { ...selectedPath.style, ...styleUpdate }

    updateShapeStyle(selectedPath.id, {
      stroke_color: updatedStyle.strokeColor,
      fill_color: updatedStyle.fillColor,
      stroke_width: updatedStyle.strokeWidth,
      fill_opacity: updatedStyle.fillOpacity,
    })

    if (newlyCreatedPath) {
      setNewlyCreatedPath(prev => ({ ...prev, style: updatedStyle }))
      setSelectedPath(prev => ({ ...prev, style: updatedStyle }))
    } else {
      setPaths(prev => prev.map(p =>
        p.id === selectedPath.id ? { ...p, style: updatedStyle } : p
      ))
      setSelectedPath(prev => ({ ...prev, style: updatedStyle }))

      if (mapId && typeof selectedPath.id === 'number') {
        try {
          await apiService.updateShape(mapId, selectedPath.id, {
            stroke_color: updatedStyle.strokeColor,
            fill_color: updatedStyle.fillColor,
            stroke_width: updatedStyle.strokeWidth,
            fill_opacity: updatedStyle.fillOpacity,
            stroke_style: updatedStyle.strokeStyle || 'solid',
            animation: updatedStyle.animation || 'none',
            show_label: updatedStyle.showLabel !== false, // Default true
          })
        } catch (error) {
          console.error('Failed to update style:', error)
        }
      }
    }
  }

  // Toggle path visibility
  const handleToggleVisibility = (pathId) => {
    setPaths(prev => prev.map(p => {
      if (p.id === pathId) {
        const newVisible = !p.visible
        setShapeVisibility?.(pathId, newVisible)
        return { ...p, visible: newVisible }
      }
      return p
    }))
  }

  // Select path from list
  const handleSelectPath = (path) => {
    setSelectedPath(path)
    setShowStylePanel(true)
    fitToShape?.(path.id)
  }

  // Update path name
  const handleUpdatePathName = async (pathId, newName) => {
    setPaths(prev => prev.map(p =>
      p.id === pathId ? { ...p, name: newName } : p
    ))
    setEditingPathName(null)

    if (mapId && typeof pathId === 'number') {
      try {
        await apiService.updateShape(mapId, pathId, { name: newName })
      } catch (error) {
        console.error('Failed to update name:', error)
      }
    }
  }

  // Delete path
  const handleDeletePath = async (pathToDelete = selectedPath) => {
    if (!pathToDelete) return

    if (newlyCreatedPath && pathToDelete.id === newlyCreatedPath.id) {
      handleCancelNewPath()
      return
    }

    if (!confirm('Xóa đường vẽ này?')) return

    try {
      if (mapId && typeof pathToDelete.id === 'number') {
        await apiService.deleteShape(mapId, pathToDelete.id)
      }
      removeShape(pathToDelete.id)
      setPaths(prev => prev.filter(p => p.id !== pathToDelete.id))
      toast.success('Đã xóa')

      if (selectedPath?.id === pathToDelete.id) {
        setShowStylePanel(false)
        setSelectedPath(null)
      }
    } catch (error) {
      console.error('Failed to delete:', error)
      toast.error('Không thể xóa')
    }
  }

  return (
    <div className="relative w-full h-full">
      {/* Main Map */}
      <LeafletMarketMapEditor
        ref={leafletMapRef}
        markers={markers}
        utilityMarkers={utilityMarkers}
        center={center}
        zoom={zoom}
        mapStyle={mapStyle}
        showCenterMarker={showCenterMarker}
        onCenterChange={onCenterChange}
        onZoomChange={onZoomChange}
        onMarkerClick={onMarkerClick}
        onUtilityMarkerClick={onUtilityMarkerClick}
        onTempMarkerConfirm={onTempMarkerConfirm}
        onMoveEnd={onMoveEnd}
        addingMarker={addingMarker}
        distanceLine={distanceLine}
      />

      {/* Drawing Toolbar */}
      <DrawingToolbar
        isDrawing={isDrawing}
        pointCount={pointCount}
        canSnapClose={canSnapClose}
        onStartDrawing={handleStartDrawing}
        onFinish={finishDrawing}
        onCancel={cancelDrawing}
        disabled={!mapId}
      />

      {/* Paths Panel Toggle Button */}
      <button
        onClick={() => setShowPathsPanel(!showPathsPanel)}
        className={clsx(
          'absolute top-4 right-4 z-[1000] p-2 rounded-lg shadow-lg transition-colors',
          showPathsPanel ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
        )}
        title="Quản lý đường vẽ"
      >
        {showPathsPanel ? <HiOutlineChevronRight className="w-5 h-5" /> : <HiOutlineChevronLeft className="w-5 h-5" />}
      </button>

      {/* Paths Management Panel */}
      {showPathsPanel && (
        <div className="absolute top-0 right-0 h-full w-72 bg-white shadow-xl z-[999] flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Đường vẽ ({paths.length})</h3>
            <button
              onClick={() => setShowPathsPanel(false)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <HiOutlineX className="w-4 h-4" />
            </button>
          </div>

          {/* Paths List */}
          <div className="flex-1 overflow-y-auto">
            {paths.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <p>Chưa có đường vẽ nào</p>
                <p className="mt-2 text-xs">Click nút vẽ ở bên trái để bắt đầu</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {paths.map((path) => (
                  <div
                    key={path.id}
                    className={clsx(
                      'p-3 hover:bg-gray-50 cursor-pointer transition-colors',
                      selectedPath?.id === path.id && 'bg-primary-50 border-l-4 border-primary-500'
                    )}
                    onClick={() => handleSelectPath(path)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div
                        className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: path.style.fillColor, borderColor: path.style.strokeColor, borderWidth: 2 }}
                      >
                        {path.shapeType === 'polygon' ? (
                          <TbPolygon className="w-4 h-4" style={{ color: path.style.strokeColor }} />
                        ) : (
                          <TbLine className="w-4 h-4" style={{ color: path.style.strokeColor }} />
                        )}
                      </div>

                      {/* Name & Type */}
                      <div className="flex-1 min-w-0">
                        {editingPathName === path.id ? (
                          <input
                            type="text"
                            defaultValue={path.name}
                            autoFocus
                            className="w-full text-sm border rounded px-2 py-1"
                            onBlur={(e) => handleUpdatePathName(path.id, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdatePathName(path.id, e.target.value)
                              }
                              if (e.key === 'Escape') {
                                setEditingPathName(null)
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {path.name}
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          {path.shapeType === 'polygon' ? 'Vùng khép kín' : 'Đường'}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setEditingPathName(path.id)}
                          className="p-1.5 hover:bg-gray-200 rounded text-gray-500"
                          title="Đổi tên"
                        >
                          <HiOutlinePencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleToggleVisibility(path.id)}
                          className="p-1.5 hover:bg-gray-200 rounded text-gray-500"
                          title={path.visible ? 'Ẩn' : 'Hiện'}
                        >
                          {path.visible ? (
                            <HiOutlineEye className="w-3.5 h-3.5" />
                          ) : (
                            <HiOutlineEyeOff className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeletePath(path)}
                          className="p-1.5 hover:bg-red-100 rounded text-red-500"
                          title="Xóa"
                        >
                          <HiOutlineTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="p-3 border-t bg-gray-50">
            <button
              onClick={handleStartDrawing}
              disabled={!mapId || isDrawing}
              className={clsx(
                'w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors',
                !mapId || isDrawing
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              )}
            >
              + Vẽ đường mới
            </button>
          </div>
        </div>
      )}

      {/* Style Panel - shown after drawing or when editing */}
      {showStylePanel && selectedPath && (
        <div
          className={clsx(
            'absolute top-1/2 -translate-y-1/2 z-[1000] w-64 bg-white rounded-xl shadow-xl overflow-hidden',
            showPathsPanel ? 'right-80' : 'right-4'
          )}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HiOutlineColorSwatch className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-900">
                {newlyCreatedPath ? 'Tùy chỉnh' : 'Chỉnh sửa'}
              </span>
            </div>
            <button
              onClick={() => {
                if (newlyCreatedPath) {
                  handleCancelNewPath()
                } else {
                  setShowStylePanel(false)
                  setSelectedPath(null)
                }
              }}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <HiOutlineX className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Path type indicator */}
            <div className="text-sm text-gray-600">
              {selectedPath.shapeType === 'polygon' ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-700 rounded">
                  <TbPolygon className="w-4 h-4" />
                  Vùng (khép kín)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-100 text-green-700 rounded">
                  <TbLine className="w-4 h-4" />
                  Đường
                </span>
              )}
            </div>

            {/* Quick color presets */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                Màu sắc
              </label>
              <div className="grid grid-cols-4 gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => handleUpdateStyle({
                      strokeColor: color.stroke,
                      fillColor: color.fill,
                    })}
                    className={clsx(
                      'w-full aspect-square rounded-lg border-2 transition-all hover:scale-105',
                      selectedPath.style.strokeColor === color.stroke
                        ? 'border-gray-800 ring-2 ring-gray-300'
                        : 'border-transparent'
                    )}
                    style={{ backgroundColor: color.fill, borderColor: color.stroke }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* Stroke width */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                Độ dày đường ({selectedPath.style.strokeWidth}px)
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={selectedPath.style.strokeWidth}
                onChange={(e) => handleUpdateStyle({ strokeWidth: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
            </div>

            {/* Fill opacity (for polygons) */}
            {selectedPath.shapeType === 'polygon' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">
                  Độ trong suốt ({Math.round(selectedPath.style.fillOpacity * 100)}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={selectedPath.style.fillOpacity * 100}
                  onChange={(e) => handleUpdateStyle({ fillOpacity: parseInt(e.target.value) / 100 })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                />
              </div>
            )}

            {/* Stroke style */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                Kiểu đường
              </label>
              <div className="flex gap-1">
                {STROKE_STYLES.map((style) => (
                  <button
                    key={style.value}
                    onClick={() => handleUpdateStyle({ strokeStyle: style.value })}
                    className={clsx(
                      'flex-1 py-2 px-2 text-xs font-medium rounded-lg transition-colors',
                      selectedPath.style.strokeStyle === style.value
                        ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    )}
                    title={style.label}
                  >
                    {style.icon}
                  </button>
                ))}
              </div>
            </div>

            {/* Animation */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                Hiệu ứng
              </label>
              <select
                value={selectedPath.style.animation || 'none'}
                onChange={(e) => handleUpdateStyle({ animation: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {ANIMATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Show Label Toggle - Chỉ cho polyline/đường */}
            {selectedPath.shapeType === 'polyline' && (
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-xs font-medium text-gray-700">Hiển thị tên</span>
                  <p className="text-[10px] text-gray-400">Tắt nếu chỉ là đường đi</p>
                </div>
                <button
                  onClick={() => handleUpdateStyle({ showLabel: !selectedPath.style.showLabel })}
                  className={clsx(
                    'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                    selectedPath.style.showLabel !== false ? 'bg-primary-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                      selectedPath.style.showLabel !== false ? 'translate-x-5' : 'translate-x-1'
                    )}
                  />
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-4 py-3 bg-gray-50 border-t flex gap-2">
            {newlyCreatedPath ? (
              <>
                <button
                  onClick={handleCancelNewPath}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Hủy
                </button>
                <button
                  onClick={handleConfirmNewPath}
                  className="flex-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Lưu
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleDeletePath()}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Xóa"
                >
                  <HiOutlineTrash className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setShowStylePanel(false)
                    setSelectedPath(null)
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900"
                >
                  Xong
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
})

export default AdvancedMapEditor
